// Overview / dashboard screen — aggregated KPIs, 6-month bar chart, recent activity,
// and quick-action nav buttons. Calls useOverview() for all data; renders skeleton cards
// during loading and a compact error state on failure.

import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import {
  Users,
  FileText,
  AlertTriangle,
  Wallet,
  Store,
  Star,
  TrendingUp,
  Upload,
  Bell,
  ArrowRight,
} from "lucide-react";

import { useOverview } from "./hooks";
import { KpiCard } from "./components/KpiCard";
import { MiniBars } from "./components/MiniBars";
import { formatDate } from "@/shared/lib/time";

// Format a 2-decimal money string to thousands-separated integer display.
// e.g. "742150000.00" → "742 150 000"
function formatMoney(val: string): string {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return val;
  return Number(n).toLocaleString();
}

// Notification status pill — separate from CustomerStatus StatusBadge
// since notification statuses ("sent" / "scheduled") differ from customer statuses.
function NotifStatusPill({ status }: { status: string }) {
  const cls =
    status === "sent"
      ? "bg-green-bg text-green-deep"
      : "bg-grey-100 text-grey-600";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill px-2 py-[3px] text-[11.5px] font-semibold leading-[1.4] ${cls}`}
    >
      <span className="h-1.5 w-1.5 flex-none rounded-pill bg-current" />
      {status}
    </span>
  );
}

// ── Skeleton card row (placeholder during loading) ─────────────────────────────

function KpiSkeleton() {
  return (
    <div className="rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <div className="h-8 w-8 animate-pulse rounded-[8px] bg-grey-100" />
        <div className="h-3 w-20 animate-pulse rounded-[4px] bg-grey-100" />
      </div>
      <div className="mb-2 h-7 w-28 animate-pulse rounded-[6px] bg-grey-100" />
      <div className="h-3 w-16 animate-pulse rounded-[4px] bg-grey-100" />
    </div>
  );
}

// ── Main screen ────────────────────────────────────────────────────────────────

export function OverviewScreen() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useOverview();

  // ── Error state ──────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-20 text-center">
        <AlertTriangle size={32} className="mb-3 text-red-bright" strokeWidth={1.75} />
        <p className="text-[15px] font-semibold text-grey-800">{t("overview.loadError")}</p>
        <p className="mt-1 text-[13px] text-grey-500">{t("overview.loadErrorHint")}</p>
      </div>
    );
  }

  // ── Derived values (safe to read when data exists) ───────────────────────────
  const customersTotal = data?.customers.total ?? 0;
  const customersNewThisMonth = data?.customers.new_this_month ?? 0;
  const agreementsActive = data?.agreements.active ?? 0;
  const agreementsNewThisMonth = data?.agreements.new_this_month ?? 0;
  const overdueCount = data?.overdue.count ?? 0;
  const overdueAmount = data ? formatMoney(data.overdue.amount) : "—";
  const outstandingDebt = data ? formatMoney(data.agreements.outstanding_debt) : "—";
  const merchantsTotal = data?.merchants.total ?? 0;
  const merchantsClients = data?.merchants.total_clients ?? 0;
  const premiumCount = data?.customers.premium ?? 0;
  const circulating = data ? formatMoney(data.money.circulating) : "—";
  const profit = data ? formatMoney(data.money.profit) : "—";

  // Build MiniBars data from the last-6-months series (contracts count).
  const barsData = (data?.series ?? []).map((s) => ({
    // Show only "MM" portion of "YYYY-MM" as the month label.
    label: s.month.slice(5),
    value: s.contracts,
  }));

  const recentNotifs = data?.recent.notifications ?? [];
  const recentCustomers = data?.recent.customers ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div>
        <h1 className="text-[20px] font-bold text-grey-900">{t("overview.title")}</h1>
        <p className="mt-0.5 text-[13.5px] text-grey-500">{t("overview.subtitle")}</p>
      </div>

      {/* ── Row 1 — 4 primary KPI cards ────────────────────────────────────────── */}
      {/* Phones: 2-up grid; the long so'm amounts take a full row so they never wrap. */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)
        ) : (
          <>
            <KpiCard
              label={t("overview.customers")}
              value={customersTotal}
              sub={`+${customersNewThisMonth} ${t("overview.newThisMonth")}`}
              icon={Users}
            />
            <KpiCard
              label={t("overview.activeAgreements")}
              value={agreementsActive}
              sub={`+${agreementsNewThisMonth} ${t("overview.newThisMonth")}`}
              icon={FileText}
            />
            <KpiCard
              label={t("overview.overdue")}
              value={overdueCount}
              sub={`${overdueAmount} ${t("overview.som")}`}
              icon={AlertTriangle}
              tone="danger"
            />
            <KpiCard
              label={t("overview.outstandingDebt")}
              value={`${outstandingDebt} ${t("overview.som")}`}
              icon={Wallet}
              className="col-span-2 md:col-span-1"
            />
          </>
        )}
      </div>

      {/* ── Row 2 — 3 secondary KPI cards ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <KpiSkeleton key={i} />)
        ) : (
          <>
            <KpiCard
              label={t("overview.merchants")}
              value={merchantsTotal}
              sub={`${merchantsClients} ${t("overview.clientsSuffix")}`}
              icon={Store}
            />
            <KpiCard
              label={t("overview.premium")}
              value={premiumCount}
              icon={Star}
            />
            <KpiCard
              label={t("overview.circulating")}
              value={`${circulating} ${t("overview.som")}`}
              sub={`${t("overview.profit")} ${profit} ${t("overview.som")}`}
              icon={TrendingUp}
              className="col-span-2 md:col-span-1"
            />
          </>
        )}
      </div>

      {/* ── Row 3 — chart (left 2/3) + recent activity (right 1/3) ─────────────── */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Left: 6-month bar chart */}
        <div className="rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-5 xl:col-span-2">
          <h2 className="mb-4 text-[14px] font-semibold text-grey-800">{t("overview.last6Months")}</h2>
          {isLoading ? (
            <div className="flex h-[120px] items-end gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 animate-pulse rounded-t-[4px] bg-grey-100"
                  style={{ height: `${30 + i * 10}%` }}
                />
              ))}
            </div>
          ) : barsData.length > 0 ? (
            <MiniBars data={barsData} />
          ) : (
            <p className="py-10 text-center text-[13px] text-grey-400">{t("overview.empty")}</p>
          )}
        </div>

        {/* Right: recent activity */}
        <div className="rounded-card border border-grey-200 bg-white p-4 shadow-card sm:p-5">
          <h2 className="mb-4 text-[14px] font-semibold text-grey-800">{t("overview.recentActivity")}</h2>

          {/* Recent notifications sub-section */}
          <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-grey-400">
            {t("overview.recentNotifications")}
          </p>
          {isLoading ? (
            <div className="mb-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-[8px] bg-grey-100" />
              ))}
            </div>
          ) : recentNotifs.length === 0 ? (
            <p className="mb-4 text-[12.5px] text-grey-400">{t("overview.empty")}</p>
          ) : (
            <ul className="mb-4 space-y-2">
              {recentNotifs.map((n) => (
                <li key={n.id} className="flex items-start justify-between gap-2">
                  <span className="min-w-0 truncate text-[12.5px] text-grey-700">{n.title}</span>
                  <div className="flex flex-none flex-col items-end gap-0.5">
                    <NotifStatusPill status={n.status} />
                    <span className="text-[11px] text-grey-400">{formatDate(n.date)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Recent customers sub-section */}
          <p className="mb-2 text-[11.5px] font-semibold uppercase tracking-wider text-grey-400">
            {t("overview.recentCustomers")}
          </p>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-8 animate-pulse rounded-[8px] bg-grey-100" />
              ))}
            </div>
          ) : recentCustomers.length === 0 ? (
            <p className="text-[12.5px] text-grey-400">{t("overview.empty")}</p>
          ) : (
            <ul className="space-y-2">
              {recentCustomers.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-[12.5px] font-medium text-grey-700">{c.name}</span>
                  <span className="flex-none text-[11px] text-grey-400">{formatDate(c.date_joined)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Row 4 — quick actions ───────────────────────────────────────────────── */}
      <div>
        <p className="mb-3 text-[13px] font-semibold text-grey-500">{t("overview.quickActions")}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <NavLink
            to="/import"
            className="flex items-center gap-3 rounded-card border border-grey-200 bg-white px-4 py-3.5 shadow-card transition-[color,background-color,border-color,transform] hover:border-green hover:bg-green-bg-soft active:scale-[0.98] sm:px-5 sm:py-4"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-green-bg text-green">
              <Upload size={18} strokeWidth={1.75} />
            </span>
            <span className="text-[14px] font-semibold text-grey-800">{t("overview.qaImport")}</span>
            <ArrowRight size={15} className="ml-auto text-grey-300" />
          </NavLink>

          <NavLink
            to="/notifications"
            className="flex items-center gap-3 rounded-card border border-grey-200 bg-white px-4 py-3.5 shadow-card transition-[color,background-color,border-color,transform] hover:border-green hover:bg-green-bg-soft active:scale-[0.98] sm:px-5 sm:py-4"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-green-bg text-green">
              <Bell size={18} strokeWidth={1.75} />
            </span>
            <span className="text-[14px] font-semibold text-grey-800">{t("overview.qaNotify")}</span>
            <ArrowRight size={15} className="ml-auto text-grey-300" />
          </NavLink>

          <NavLink
            to="/customers"
            className="flex items-center gap-3 rounded-card border border-grey-200 bg-white px-4 py-3.5 shadow-card transition-[color,background-color,border-color,transform] hover:border-green hover:bg-green-bg-soft active:scale-[0.98] sm:px-5 sm:py-4"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-green-bg text-green">
              <Users size={18} strokeWidth={1.75} />
            </span>
            <span className="text-[14px] font-semibold text-grey-800">{t("overview.qaCustomers")}</span>
            <ArrowRight size={15} className="ml-auto text-grey-300" />
          </NavLink>
        </div>
      </div>
    </div>
  );
}
